import { InfoOutlined } from '@mui/icons-material';
import { Checkbox, FormControlLabel, Icon, InputAdornment, Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { L, UNITS, setCurrentUnitSystem } from 'harmony-language';
import React, { FormEvent } from 'react';
import { OrgQueryKeys } from '../../../../api/config';
import { useOrganizationQuery } from '../../../../api/queries/use-organization-query';
import { useTractorTrailer } from '../../../../api/queries/use-tractor-trailer';
import { useUser } from '../../../../api/queries/use-user';
import { Driver, SiteManager } from '../../../../constants/constants';
import { Contact, ContactNotificationMethod, DriverProperty, NOTIFICATION_TYPES, Organization, Role } from '../../../../types';
import { toIdLabelTuple } from '../../../../utils/data-mapping-utils';
import { setTimezone } from '../../../../utils/date-time-utils';
import { isNothing } from '../../../../utils/string-utils';
import { createAgisticsSelectElements } from '../../../shared/agistics-select';
import { useDebounce } from '../../../shared/hooks/use-debounce';
import { CheckboxEnabled } from '../../../shared/inputs/checkbox-enabled';
import { Select } from '../../../shared/inputs/select';
import { ValidatedTextField } from '../../../shared/inputs/validated-text-field';
import { AgisticsMultiSelect } from '../../../shared/multi-select/agistics-multi-select';
import { fetchLanguageDictionary } from '../../../user/actions/fetch-language-dictionary';
import { isPasswordValid } from '../../../user/user-activation';
import { EditCreateContact } from '../../contacts/edit-create-contact';
import { HomeLocationSelect } from './home-location-select';
import { SelectUserRoles } from './select-user-roles';
import { useLookupUsername } from './use-lookup-username';

export const userHasEmail = (notificationMethods: ContactNotificationMethod[]): boolean => {
    if (isNothing(notificationMethods?.find(x => x.notificationType === NOTIFICATION_TYPES.Email)?.destination)) {
        return false;
    }
    return true;
}

type ShimInteractiveUser = {
    id?: number;
    contact: Contact;
    contactId?: number;
    email?: string | null;
    phone?: string | null;
    enabled: boolean;
    firstName?: string;
    lastName?: string;
    name?: string;
    organization?: Organization;
    organizationId?: number;
    roles?: Role[];
    username: string;
    driverInfo?: {
        defaultTractorId?: number | null;
        defaultTrailerId?: number | null;
        contract?: boolean;
        loadCompletionOutOfSequence?: boolean;
        createRoute?: boolean;
        nextLoad?: boolean;
        homeLocationId?: number | null;
        driverPropertyIds?: number[];
    }
    password?: string;
    sublocationTagging?: boolean;
};

type EditCreateUserProps = {
    existingUser: ShimInteractiveUser;
    onSubmit: (user: ShimInteractiveUser) => void;
};

export const EditCreateUser = (props: EditCreateUserProps) => {
    const { existingUser, onSubmit } = props;
    const { data: driverProperties = [] } = useOrganizationQuery<DriverProperty[]>(OrgQueryKeys.driverProperties);

    const [user, setUser] = React.useState<ShimInteractiveUser>(existingUser || {
        contact: null,
        enabled: true,
        username: ''
    });
    const [isDriverInfoAvailable, setIsDriverInfoAvailable] = React.useState(false);
    const [isSublocationTaggingAvailable, setIsSublocationTaggingAvailable] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const [usernameValid, setIsUsernameValid] = React.useState(true);
    const [emailHasValue, setEmailHasValue] = React.useState(userHasEmail(user.contact?.contactNotificationMethods));
    const [showPasswordField, setShowPasswordField] = React.useState(false);
    
    const { tractors, trailers, organizationId } = useTractorTrailer();
    const { debouncedValue, isDebouncing } = useDebounce(search, 2000);
    const { isLoading, fetch } = useLookupUsername();
    const { user: { id: selfId } } = useUser();
    
    const tractorList = (tractors?.filter(x => x.organizationId === organizationId)?.map(toIdLabelTuple('userDisplayName')) || []).concat({ id: null, label: L.none() });
    const trailerList = (trailers?.filter(x => x.organizationId === organizationId)?.map(toIdLabelTuple('userDisplayName')) || []).concat({ id: null, label: L.none() });
    const isEditingSelf = React.useMemo(() =>  user?.id === selfId ? true : false, []);
    const driverPropertyOptions = createAgisticsSelectElements(driverProperties, (dp) => dp.name);

    React.useEffect(() => {
        if (search && existingUser?.username !== search) {
            const fetchData = async () => {
                const validUsername = await fetch(debouncedValue);
                setIsUsernameValid(validUsername);
            }

            fetchData();
        } else {
            setIsUsernameValid(true);
        }
    }, [debouncedValue]);

    React.useEffect(() => {
        setIsDriverInfoAvailable(Boolean(user.roles?.some(r => r.id === Driver.id)));
        const canTagSublocations = Boolean(user.roles?.some(r => r.id === Driver.id || r.id === SiteManager.id));
        setIsSublocationTaggingAvailable(canTagSublocations);
        if (!canTagSublocations && user.sublocationTagging) {
            setUser({
                ...user,
                sublocationTagging: false,
            });
        }
    }, [user]);

    const submitForm = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (isEditingSelf) {
            setTimezone(user.contact.timezone);
            fetchLanguageDictionary(user.contact.language || undefined);
            setCurrentUnitSystem(user.contact.units || user.organization?.units || UNITS.Imperial);
        }
        if (userHasEmail(user.contact.contactNotificationMethods)) {
            delete user.password;
        }

        onSubmit(user);
    };

    const onContactChanged = (contact: Contact) => {
        const hasEmail = userHasEmail(contact.contactNotificationMethods);
        if (hasEmail) user.password = '';
        setEmailHasValue(hasEmail);
        setUser({ ...user, contact: contact });
    };

    return (
        <div style={{ maxWidth: '40rem', marginTop: '1rem' }}>
            <form onSubmit={submitForm}>
                <GridContainer id='username-password-container'>
                    <EditCreateUserItem>
                        <TextField
                            label={L.username()}
                            variant='standard'
                            fullWidth
                            required={true}
                            error={!usernameValid}
                            helperText={!usernameValid && L.usernameTaken()}
                            value={user.username}
                            onChange={(e) => {
                                setUser({ ...user, username: e.target.value });
                                setSearch(e.target.value);
                            }}
                        />
                    </EditCreateUserItem>
                    {!emailHasValue ? (!showPasswordField ? (
                        <EditCreateUserItem>
                            <Button
                                variant='contained'
                                onClick={() => setShowPasswordField(true)}
                                sx={{ mt: '16px' }}
                            >
                                {L.setPassword()}
                            </Button>
                        </EditCreateUserItem>
                    ) : (
                        <EditCreateUserItem>
                            <ValidatedTextField
                                label={L.password()}
                                type='password'
                                autoComplete='new-password'
                                fullWidth
                                required={true}
                                validations={[{ message: L.passwordInvalid(), isValid: () => user?.password ? isPasswordValid(user.password) : false }]}
                                value={user?.password}
                                InputProps={{
                                    endAdornment:
                                        <InputAdornment position='end'>
                                            <Tooltip
                                                placement={'top'}
                                                title={L.passwordRequirements()}>
                                                    <Icon><InfoOutlined></InfoOutlined></Icon>
                                            </Tooltip>
                                        </InputAdornment>,
                                }}
                                onChange={(e) => {
                                    setUser({ ...user, password: e.target.value });
                                }}
                            />
                        </EditCreateUserItem>
                    )) : (<EditCreateUserItem></EditCreateUserItem>)}
                    <EditCreateUserItem>
                        <SelectUserRoles roles={user.roles} onChange={(newRoles) => {
                            setUser({ ...user, roles: newRoles });
                        }}/>
                    </EditCreateUserItem>
                    <EditCreateUserItem>
                        <div style={{ marginTop: '16px' }}>
                            <CheckboxEnabled
                                checked={user.enabled}
                                disabled={isEditingSelf ? true : false}
                                onChange={() => setUser({ ...user, enabled: !user.enabled })}
                            />
                        </div>
                    </EditCreateUserItem>
                </GridContainer>
                <GridContainer id='contact-info-container'>
                    <EditCreateContact
                        contact={user.contact}
                        onContactChanged={onContactChanged}
                    />
                </GridContainer>
                {isDriverInfoAvailable &&
                    <GridContainer id='driver-info-container'>
                        <EditCreateUserItem>
                            <Select
                                label={L.defaultTractor()}
                                item={tractorList.find(tractor => tractor.id === user.driverInfo?.defaultTractorId)}
                                list={tractorList}
                                onChange={(value) => setUser({...user, driverInfo: {
                                    ...user.driverInfo,
                                    defaultTractorId: value.id
                                }})}
                            />
                        </EditCreateUserItem>
                        <EditCreateUserItem>
                            <Select
                                label={L.defaultTrailer()}
                                item={trailerList.find(trailer => trailer.id === user.driverInfo?.defaultTrailerId)}
                                list={trailerList}
                                onChange={(value) => setUser({
                                    ...user, driverInfo: {
                                        ...user.driverInfo,
                                        defaultTrailerId: value.id
                                    }
                                })}
                            />
                        </EditCreateUserItem>
                        <EditCreateUserItem>
                            <HomeLocationSelect
                            homeLocationId={user.driverInfo?.homeLocationId}
                            onChange={(homeLocationId) => setUser({
                                ...user,
                                driverInfo: {
                                    ...user.driverInfo,
                                    homeLocationId: homeLocationId,
                                }
                            })}
                        />
                        </EditCreateUserItem>
                        <EditCreateUserItem>
                            <AgisticsMultiSelect
                            label={L.driverProperties()}
                            items={driverPropertyOptions}
                            selectedIds={user.driverInfo?.driverPropertyIds || []}
                            setSelectedIds={(ids) => setUser({
                                ...user,
                                driverInfo: {
                                    ...user.driverInfo,
                                    driverPropertyIds: ids
                                }
                            })}
                        />
                        </EditCreateUserItem>
                        <Grid id='driver-info-booleans' container item columns={2} columnSpacing={2}>
                            <EditCreateUserItem>
                                <FormControlLabel
                                    label={L.contract()}
                                    control={
                                        <Checkbox
                                            checked={user.driverInfo?.contract || false}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                setUser({...user, driverInfo: {
                                                    ...user.driverInfo,
                                                    contract: event.target.checked,
                                                }})
                                            }}
                                        />
                                    }
                                />
                            </EditCreateUserItem>
                            <EditCreateUserItem>
                                <FormControlLabel
                                    label={L.loadsOutOfSequence()}
                                    control={
                                        <Checkbox
                                            checked={user.driverInfo?.loadCompletionOutOfSequence || false}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                setUser({...user, driverInfo: {
                                                    ...user.driverInfo,
                                                    loadCompletionOutOfSequence: event.target.checked,
                                                }})
                                            }}
                                        />
                                    }
                                />
                            </EditCreateUserItem>
                            <EditCreateUserItem>
                                <FormControlLabel
                                    label={L.createRoute()}
                                    control={
                                        <Checkbox
                                            checked={user.driverInfo?.createRoute || false}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                setUser({...user, driverInfo: {
                                                    ...user.driverInfo,
                                                    createRoute: event.target.checked,
                                                }})
                                            }}
                                        />
                                    }
                                />
                            </EditCreateUserItem>
                            <EditCreateUserItem>
                                <FormControlLabel
                                    label={L.nextLoad()}
                                    style={{ paddingRight: '20px' }}
                                    control={
                                        <Checkbox
                                            checked={user.driverInfo?.nextLoad || false}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                setUser({...user, driverInfo: {
                                                    ...user.driverInfo,
                                                    nextLoad: event.target.checked,
                                                }})
                                            }}
                                        />
                                    }
                                />
                            </EditCreateUserItem>
                        </Grid>
                    </GridContainer>
                }
                {isSublocationTaggingAvailable &&
                    <FormControlLabel
                        label={L.sublocationTagging()}
                        control={
                            <Checkbox
                                checked={user?.sublocationTagging || false}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    setUser({...user, sublocationTagging: event.target.checked})
                                }}
                            />
                        }
                    />
                }
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        type='submit'
                        title={L.save()}
                        data-testid='user-submit-button'
                        disabled={isDebouncing || !usernameValid || isLoading}
                        variant='contained'
                    >
                        {existingUser ? L.updateUser() : L.createUser()}
                    </Button>
                </div>
            </form>
        </div>
    );
};

const GridContainer = (props: React.PropsWithChildren<{id: string}>) => (
    <Grid id={props.id} container columns={2} spacing={2} sx={{ pb: 2 }}>
        {props.children}
    </Grid>
)

export const EditCreateUserItem = (props: React.PropsWithChildren) => (
    <Grid item xs={2} md={1}>
        {props.children}
    </Grid>
)